import React, { useEffect, useState } from "react";
import {
	Alert,
	Button,
	Chip,
	Collapse,
	Divider,
	Drawer,
	FormControl,
	Grid,
	IconButton,
	Input,
	InputLabel,
	LinearProgress,
	ListItemText,
	MenuItem,
	Paper,
	Select,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import CloseIcon from "@mui/icons-material/Close";

import { getPartesGrupoFiltros } from "../../services/apis";
import { getAuthStorageUserScope } from "../../services/authStorage";

const CUADRILLAS_ADVANCED_FILTERS_SESSION_KEY = "planaett:cuadrillas:advanced-filters";
const buildUserSessionKey = (baseKey) =>
	`${baseKey}:${encodeURIComponent(getAuthStorageUserScope())}`;

const readUserScopedSessionValue = (baseKey) => {
	const scopedValue = window.sessionStorage.getItem(buildUserSessionKey(baseKey));
	return scopedValue !== null ? scopedValue : window.sessionStorage.getItem(baseKey);
};

const writeUserScopedSessionValue = (baseKey, value) => {
	window.sessionStorage.setItem(buildUserSessionKey(baseKey), value);
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 300,
		},
	},
};

const getOptionValue = (option) =>
	String(option?.value ?? option?.nombre ?? "");

const getOptionLabel = (option) =>
	String(option?.label ?? option?.nombre ?? option?.value ?? "");

const getEffectiveSelectedValues = (filterItem) =>
	(Array.isArray(filterItem?.seleccionado) ? filterItem.seleccionado : []).filter(
		(value) => String(value) !== "__all__"
	);

const readPersistedSelectedFilters = () => {
	try {
		const rawValue = readUserScopedSessionValue(CUADRILLAS_ADVANCED_FILTERS_SESSION_KEY);
		if (!rawValue) {
			return [];
		}

		const parsedValue = JSON.parse(rawValue);
		if (!Array.isArray(parsedValue)) {
			return [];
		}

		return parsedValue
			.filter((item) => typeof item?.key === "string" && item.key.trim() !== "")
			.map((item) => ({
				key: item.key,
				seleccionado: Array.isArray(item?.seleccionado)
					? item.seleccionado.map((value) => String(value))
					: [],
			}));
	} catch (error) {
		return [];
	}
};

const persistSelectedFilters = (filters = []) => {
	try {
		const payload = (Array.isArray(filters) ? filters : [])
			.filter((item) => typeof item?.key === "string" && item.key.trim() !== "")
			.map((item) => ({
				key: item.key,
				seleccionado: Array.isArray(item?.seleccionado)
					? item.seleccionado.map((value) => String(value))
					: [],
			}));

		writeUserScopedSessionValue(
			CUADRILLAS_ADVANCED_FILTERS_SESSION_KEY,
			JSON.stringify(payload)
		);
	} catch (error) {
		// Ignore session persistence failures in the UI.
	}
};

const mergeSelectedFilters = (nextFilters = [], previousFilters = []) =>
	(Array.isArray(nextFilters) ? nextFilters : []).map((filterItem) => {
		const previousFilter = (Array.isArray(previousFilters) ? previousFilters : []).find(
			(item) => item?.key === filterItem?.key
		);
		const availableValues = new Set(
			(Array.isArray(filterItem?.valores) ? filterItem.valores : []).map((option) =>
				getOptionValue(option)
			)
		);
		const selectedValues = (Array.isArray(previousFilter?.seleccionado)
			? previousFilter.seleccionado
			: []
		).filter((value) => availableValues.has(String(value)));

		const fallbackSelectedValues = Array.isArray(previousFilter?.seleccionado)
			? previousFilter.seleccionado.map((value) => String(value))
			: [];

		return {
			...filterItem,
			seleccionado: availableValues.size > 0 ? selectedValues : fallbackSelectedValues,
		};
	});

const buildInitialSelectedFilters = (persistedFilters = [], initialFilters = []) => {
	const normalizedPersistedFilters = Array.isArray(persistedFilters) ? persistedFilters : [];
	const normalizedInitialFilters = Array.isArray(initialFilters) ? initialFilters : [];

	return normalizedPersistedFilters.length > 0
		? mergeSelectedFilters(normalizedPersistedFilters, normalizedInitialFilters)
		: normalizedInitialFilters;
};

const collectRecolectores = (cuadrilla) => [
	...(Array.isArray(cuadrilla?.recolectores?.no_comprobados)
		? cuadrilla.recolectores.no_comprobados
		: []),
	...(Array.isArray(cuadrilla?.recolectores?.comprobados)
		? cuadrilla.recolectores.comprobados
		: []),
	...(Array.isArray(cuadrilla?.recolectores?.error_comprobacion)
		? cuadrilla.recolectores.error_comprobacion
		: []),
];

const buildEstadoGrupoValues = (cuadrilla) => {
	const pendientes = Array.isArray(cuadrilla?.recolectores?.no_comprobados)
		? cuadrilla.recolectores.no_comprobados.length
		: 0;
	const incidenciasReconocimiento = Array.isArray(cuadrilla?.recolectores?.error_comprobacion)
		? cuadrilla.recolectores.error_comprobacion.length
		: 0;
	const intentosNoPertenece = Number(cuadrilla?.datos_cuadrilla?.intentos_no_pertenece ?? 0);
	const intentosNoRegistrado = Number(cuadrilla?.datos_cuadrilla?.intentos_no_registrado ?? 0);
	const incidencias =
		incidenciasReconocimiento +
		(Number.isFinite(intentosNoPertenece) ? intentosNoPertenece : 0) +
		(Number.isFinite(intentosNoRegistrado) ? intentosNoRegistrado : 0);

	const estados = [];
	if (pendientes > 0) {
		estados.push("pendientes");
	}
	if (incidencias > 0) {
		estados.push("incidencias");
	}
	if (pendientes === 0 && incidencias === 0) {
		estados.push("completo");
	}

	return estados;
};

const buildEstadoFichajesValues = (cuadrilla) => {
	const fichados =
		(Array.isArray(cuadrilla?.recolectores?.comprobados)
			? cuadrilla.recolectores.comprobados.length
			: 0) +
		(Array.isArray(cuadrilla?.recolectores?.error_comprobacion)
			? cuadrilla.recolectores.error_comprobacion.length
			: 0);

	return fichados > 0 ? ["con_fichados"] : ["sin_fichados"];
};

const matchesFilter = (cuadrilla, filterKey, selectedValues = []) => {
	const values = (Array.isArray(selectedValues) ? selectedValues : []).map((value) =>
		String(value)
	);

	if (values.length === 0) {
		return true;
	}

	switch (filterKey) {
		case "empresas":
			return values.includes(String(cuadrilla?.datos_cuadrilla?.id_clienteG ?? ""));

		case "delegaciones": {
			const idDelegacion = Number(cuadrilla?.datos_cuadrilla?.id_delegacion ?? 0);
			return values.some((value) =>
				value === "__sin_delegacion__"
					? idDelegacion <= 0
					: String(idDelegacion) === String(value)
			);
		}

		case "cabos":
			return values.includes(String(cuadrilla?.datos_cuadrilla?.id_cabo ?? ""));

		case "operativo":
			return values.includes(String(Number(cuadrilla?.datos_cuadrilla?.operativo ?? 0)));

		case "recolectores":
			return collectRecolectores(cuadrilla).some((recolector) =>
				values.includes(String(recolector?.id_usuario ?? ""))
			);

		case "validado":
			return values.includes(String(Number(cuadrilla?.datos_cuadrilla?.validado ?? 0)));

		case "revisado":
			return values.includes(String(Number(cuadrilla?.datos_cuadrilla?.revisado ?? 0)));

		case "grabado":
			return values.includes(String(Number(cuadrilla?.datos_cuadrilla?.grabado ?? 0)));

		case "campanas": {
			const idCampana = Number(cuadrilla?.datos_cuadrilla?.id_campana ?? 0);
			return values.some((value) =>
				value === "__sin_campana__"
					? idCampana <= 0
					: String(idCampana) === String(value)
			);
		}

		case "estado_grupo": {
			const estados = buildEstadoGrupoValues(cuadrilla);
			return values.some((value) => estados.includes(value));
		}

		case "estado_fichajes": {
			const estados = buildEstadoFichajesValues(cuadrilla);
			return values.some((value) => estados.includes(value));
		}

		default:
			return true;
	}
};

export const FiltrosCuadrillas = ({
	open,
	onClose,
	setCuadrillas,
	cuadrillasOG,
	fechaDesde,
	fechaHasta,
	setCantidadFiltros,
	onFiltersChange,
	initialSelectedFilters,
	resetToken = 0,
}) => {
	const theme = useTheme();
	const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
	const [arrFiltros, setArrFiltros] = useState(() =>
		buildInitialSelectedFilters(readPersistedSelectedFilters(), initialSelectedFilters)
	);
	const [loadingFilters, setLoadingFilters] = useState(false);
	const [filtersError, setFiltersError] = useState("");

	useEffect(() => {
		if (!Array.isArray(initialSelectedFilters)) {
			return;
		}

		setArrFiltros((currentFilters) => {
			const nextFilters = Array.isArray(currentFilters) && currentFilters.length > 0
				? mergeSelectedFilters(currentFilters, initialSelectedFilters)
				: initialSelectedFilters;

			return JSON.stringify(nextFilters) === JSON.stringify(currentFilters)
				? currentFilters
				: nextFilters;
		});
	}, [initialSelectedFilters]);

	const handleChange = (index) => (event) => {
		const filterItem = arrFiltros[index] || {};
		const rawValue = event?.target?.value;
		const nextSelected = filterItem?.single
			? rawValue === "__all__" || rawValue === undefined || rawValue === null
				? []
				: [String(rawValue)]
			: typeof rawValue === "string"
				? rawValue.split(",")
				: rawValue;

		setArrFiltros((currentFilters) =>
			currentFilters.map((filterItem, filterIndex) =>
				filterIndex === index
					? {
							...filterItem,
							seleccionado: nextSelected,
					  }
					: filterItem
			)
		);
	};

	const limpiarFiltros = () => {
		setArrFiltros((currentFilters) =>
			currentFilters.map((filterItem) => ({
				...filterItem,
				seleccionado: [],
			}))
		);
		setCuadrillas(cuadrillasOG);
	};

	useEffect(() => {
		if (!resetToken) {
			return;
		}

		setArrFiltros((currentFilters) =>
			currentFilters.map((filterItem) => ({
				...filterItem,
				seleccionado: [],
			}))
		);
		setCuadrillas(cuadrillasOG);
	}, [cuadrillasOG, resetToken, setCuadrillas]);

	const borrarFiltro = (filterKey, selectedValue) => {
		setArrFiltros((currentFilters) =>
			currentFilters.map((filterItem) =>
				filterItem?.key === filterKey
					? {
							...filterItem,
							seleccionado: (Array.isArray(filterItem?.seleccionado)
								? filterItem.seleccionado
								: []
							).filter((value) => String(value) !== String(selectedValue)),
					  }
					: filterItem
			)
		);
	};

	useEffect(() => {
		if (!Array.isArray(cuadrillasOG) || cuadrillasOG.length === 0) {
			setCuadrillas([]);
			return;
		}

		const filtrosActivos = arrFiltros.filter(
			(filtro) => Array.isArray(filtro?.seleccionado) && filtro.seleccionado.length > 0
		);

		if (filtrosActivos.length === 0) {
			setCuadrillas(cuadrillasOG);
			return;
		}

		const cuadrillasFiltradas = cuadrillasOG.filter((cuadrilla) =>
			filtrosActivos.every((filtro) =>
				matchesFilter(cuadrilla, filtro.key, filtro.seleccionado)
			)
		);

		setCuadrillas(cuadrillasFiltradas);
	}, [arrFiltros, cuadrillasOG, setCuadrillas]);

	useEffect(() => {
		const total = arrFiltros.reduce(
			(accumulator, filtro) =>
				accumulator +
				getEffectiveSelectedValues(filtro).length,
			0
		);

		setCantidadFiltros(total);
		if (typeof onFiltersChange === "function") {
			onFiltersChange(arrFiltros);
		}
	}, [arrFiltros, onFiltersChange, setCantidadFiltros]);

	useEffect(() => {
		persistSelectedFilters(arrFiltros);
	}, [arrFiltros]);

	useEffect(() => {
		let active = true;

		const cargarDatosFiltros = async () => {
			setLoadingFilters(true);
			setFiltersError("");

			try {
				const response = await getPartesGrupoFiltros({
					fechaDesde,
					fechaHasta,
				});

				if (!active) {
					return;
				}

				if (response?.success === true) {
					setArrFiltros((currentFilters) =>
						mergeSelectedFilters(response?.data || [], currentFilters)
					);
					return;
				}

				setArrFiltros((currentFilters) =>
					mergeSelectedFilters([], currentFilters)
				);
				setFiltersError(
					response?.message || "No se pudieron cargar las opciones de filtros."
				);
			} catch (error) {
				console.error("Error cargando filtros de partes de grupos:", error);

				if (!active) {
					return;
				}

				setFiltersError("No se pudieron cargar las opciones de filtros.");
			} finally {
				if (active) {
					setLoadingFilters(false);
				}
			}
		};

		void cargarDatosFiltros();

		return () => {
			active = false;
		};
	}, [fechaDesde, fechaHasta]);

	const filtrosContent = (
		<Grid
			container
			direction="column"
			justifyContent="space-between"
			alignItems="stretch"
			size={{ xs: 12 }}
			sx={{
				p: 1.5,
				height: "100%",
				width: "100%",
				minWidth: 0,
				maxWidth: "none",
			}}
		>
			<Grid container direction="column" size={{ xs: 12 }}>
				<Grid
					container
					direction="row"
					justifyContent="space-between"
					alignItems="center"
					size={{ xs: 12 }}
				>
					<Typography variant="h5">Filtros</Typography>

					<IconButton onClick={onClose}>
						<CloseIcon />
					</IconButton>
				</Grid>

				<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
					Opciones cargadas desde servidor para el rango aplicado.
				</Typography>

				<Divider sx={{ my: 1.5 }} />

				{loadingFilters && <LinearProgress sx={{ mb: 1.5 }} />}

				{!!filtersError && (
					<Alert severity="warning" sx={{ mb: 2 }}>
						{filtersError}
					</Alert>
				)}

				<Grid
					container
					direction="row"
					justifyContent="start"
					alignItems="stretch"
					size={{ xs: 12 }}
					spacing={2}
				>
					{arrFiltros.map((filtro, index) => {
						const selectedValues = getEffectiveSelectedValues(filtro);
						const selectValue = filtro?.single
							? selectedValues[0] ?? "__all__"
							: selectedValues;

							return (
								<Grid
									key={filtro.key}
									item
									size={{ xs: 12, md: 6, lg: 4, xl: 3 }}
								>
									<Grid
										container
										direction="column"
										justifyContent="start"
										alignItems="stretch"
										size={{ xs: 12 }}
										sx={{ height: "100%" }}
									>
										<FormControl variant="standard" fullWidth>
											<InputLabel id={`label-${filtro.key}`}>{filtro.nombre}</InputLabel>
											<Select
												labelId={`label-${filtro.key}`}
												id={`select-${filtro.key}`}
												multiple={!filtro?.single}
												value={selectValue}
												onChange={handleChange(index)}
												input={<Input label={filtro.nombre} />}
												renderValue={(selected) =>
													(Array.isArray(selected) ? selected : [selected]).filter(
														(value) => String(value) !== "__all__"
													).length > 0
														? (Array.isArray(selected) ? selected : [selected])
															.filter((value) => String(value) !== "__all__")
															.map((value) =>
																getOptionLabel(
																	(Array.isArray(filtro?.valores) ? filtro.valores : []).find(
																		(option) =>
																			getOptionValue(option) === String(value)
																	)
																) || String(value)
															)
															.join(", ")
														: filtro?.single
															? "Todos"
															: `Seleccionar ${filtro.nombre}`
												}
												MenuProps={MenuProps}
											>
												{Array.isArray(filtro?.valores) && filtro.valores.length > 0 ? (
													filtro.valores.map((valor) => {
														const optionValue = getOptionValue(valor);
														const optionLabel = getOptionLabel(valor);

														return (
															<MenuItem key={`${filtro.key}-${optionValue}`} value={optionValue}>
																{!filtro?.single && (
																	<Checkbox checked={selectedValues.includes(optionValue)} />
																)}
																<ListItemText primary={optionLabel} />
															</MenuItem>
														);
													})
												) : (
													<MenuItem disabled>
														<ListItemText primary="Sin opciones disponibles" />
													</MenuItem>
												)}
											</Select>
										</FormControl>

										<Grid
											container
											direction="row"
											justifyContent="start"
											alignItems="start"
											size={{ xs: 12 }}
											sx={{ gap: 0.75, mt: 1, alignContent: "flex-start", minHeight: 36 }}
										>
											{selectedValues.map((value) => {
												const option = (Array.isArray(filtro?.valores) ? filtro.valores : []).find(
													(item) => getOptionValue(item) === String(value)
												);

												return (
													<Chip
														key={`${filtro.key}-chip-${value}`}
														label={getOptionLabel(option) || String(value)}
														variant="filled"
														onDelete={() => borrarFiltro(filtro.key, value)}
														color="secondary"
													/>
												);
											})}
										</Grid>
									</Grid>
								</Grid>
							);
						})}

					{!loadingFilters && arrFiltros.length === 0 && (
						<Grid size={{ xs: 12 }}>
							<Alert severity="info">
								No hay filtros configurados para este rango.
							</Alert>
						</Grid>
					)}
				</Grid>
			</Grid>

			<Grid
				container
				direction="row"
				justifyContent="flex-end"
				alignItems="center"
				size={{ xs: 12 }}
			>
				<Button
					variant="outlined"
					color="primary"
					onClick={limpiarFiltros}
					fullWidth={!isDesktop}
					disabled={loadingFilters && arrFiltros.length === 0}
				>
					Limpiar filtros
				</Button>
			</Grid>
		</Grid>
	);

	if (isDesktop) {
		return (
			<Collapse in={open} timeout="auto" unmountOnExit sx={{ width: "100%" }}>
				<Paper
					variant="outlined"
					sx={{
						mt: 1.5,
						width: "100%",
						borderRadius: 2,
						overflow: "hidden",
					}}
				>
					{filtrosContent}
				</Paper>
			</Collapse>
		);
	}

	return (
		<Drawer
			open={open}
			onClose={onClose}
			anchor="right"
			PaperProps={{
				sx: {
					width: {
						xs: "86%",
						sm: "52%",
						md: "36%",
						lg: "30%",
						xl: "24%",
					},
				},
			}}
		>
			{filtrosContent}
		</Drawer>
	);
};
